/**
 * Вкладка «Новый год» (29.10.2026): загрузка карточек, заметок и скринов из /api/ny (apps/api/src/routes/ny/*).
 * В путях — только id карточки (client_refs), не телефон и не хэш. Картинки — авторизованным fetch (apiFileUrl → objectURL):
 * <img> не шлёт Authorization, прямых ссылок на файл нет.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiFileUrl } from "../../api/client";
import { ApiException } from "../../api/types";
import type { NyCardsResp, NyFilter } from "./nyModel";
import { planBatches, shotsForm, shrinkShot, type ShrunkShot } from "./nyShrink";

export const errText = (e: unknown, fb: string): string => (e instanceof Error && e.message ? e.message : fb);

/* Память вкладки на сессию: тап по брони уводит в «Сетку» и размонтирует секцию — по возврату поиск, фильтр, раскрытая
   карточка и последний список на месте. Только память страницы (ПДн в localStorage не кладём). */
export const nyKeep: { query: string; filter: NyFilter; open: string | null; data: NyCardsResp | null } = { query: "", filter: "all", open: null, data: null };

/** Карточки сезона. Вход во вкладку — свежий список; при обновлении старый остаётся на экране (без «Загрузка…»). */
export function useNyCards() {
  const [data, setData] = useState<NyCardsResp | null>(nyKeep.data);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    api<NyCardsResp>("/api/ny/cards", { signal: ac.signal })
      .then((r) => { nyKeep.data = r; setData(r); setError(null); setLoading(false); })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        /* нет прав (сменили аккаунт без перезагрузки) — прошлый список с телефонами на экране не оставляем */
        if (e instanceof ApiException && (e.status === 401 || e.status === 403)) { nyKeep.data = null; setData(null); }
        setError(errText(e, "Не удалось загрузить карточки")); setLoading(false);
      });
    return () => ac.abort();
  }, [tick]);
  return { data, loading, error, reload };
}

/** Список по id карточки (заметки или скрины) — общий каркас. */
function useRefList<T>(path: string, key: "notes" | "shots") {
  const [items, setItems] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    const ac = new AbortController();
    api<Record<string, T[] | undefined>>(path, { signal: ac.signal })
      .then((r) => { setItems(r[key] ?? []); setError(null); })
      .catch((e: unknown) => { if (!ac.signal.aborted) setError(errText(e, "Не удалось загрузить")); });
    return () => ac.abort();
  }, [path, key, tick]);
  return { items, setItems, error, reload };
}

export interface NyNote { id: number; text: string; author_id: string | null; author_name: string | null; created_at: string; can_delete: boolean }

/** «История» клиента: лента заметок (новые сверху), добавить, удалить свою (ЕО — любую). */
export function useNyNotes(ref: number, onChanged: () => void) {
  const { items, setItems, error, reload } = useRefList<NyNote>(`/api/ny/clients/${ref}/notes`, "notes");
  const add = useCallback(async (text: string) => {
    await api(`/api/ny/clients/${ref}/notes`, { method: "POST", body: { text } });
    reload(); onChanged();
  }, [ref, reload, onChanged]);
  const remove = useCallback(async (id: number) => {
    await api(`/api/ny/notes/${id}`, { method: "DELETE" });
    setItems((p) => p?.filter((n) => n.id !== id) ?? p); onChanged();
  }, [setItems, onChanged]);
  return { notes: items, error, add, remove };
}

export interface NyShot {
  id: number; mime: string; size: number; w: number | null; h: number | null;
  uploaded_by: string | null; uploaded_by_name: string | null; created_at: string; can_delete: boolean;
}
export interface ShotsBusy { stage: "shrink" | "send"; done: number; total: number }
/** итог отправки: ушло, ждут повтора (связь, хранилище), отвергнуты сервером (4xx — повтор не поможет).
 *  Ждущие повтора могли и лечь (ответ потерялся) — повтор шлёт их с теми же ключами, сервер не задвоит. */
interface Sent { sent: number; left: ShrunkShot[]; rejected: number; err: string | null }

/** Скрины переписок: список, загрузка (ужатие → запросы по ≤ 10 файлов), повтор неотправленных, удаление своего. */
export function useNyShots(ref: number, onChanged: () => void) {
  const { items, setItems, error, reload } = useRefList<NyShot>(`/api/ny/clients/${ref}/shots`, "shots");
  const [busy, setBusy] = useState<ShotsBusy | null>(null);
  /* не ушли на сервер (связь, хранилище) — держим ужатыми, «Повторить» шлёт их же без нового ужатия */
  const [pending, setPending] = useState<ShrunkShot[]>([]);
  const [note, setNote] = useState<string | null>(null);

  const send = useCallback(async (ready: ShrunkShot[]): Promise<Sent> => {
    let sent = 0, rejected = 0, err: string | null = null;
    const left: ShrunkShot[] = [];
    setBusy({ stage: "send", done: 0, total: ready.length });
    for (const batch of planBatches(ready.map((r) => r.file.size))) {
      const fd = shotsForm(ready, batch);
      /* зависшая на мобильной связи отправка не держит экран вечно — 2 минуты на запрос */
      const ac = new AbortController(); const tm = window.setTimeout(() => ac.abort(), 120_000);
      try {
        const r = await api<{ ids?: number[]; failed_n?: number[] }>(`/api/ny/clients/${ref}/shots`, { method: "POST", body: fd, retries: 0, signal: ac.signal });
        sent += r.ids?.length ?? 0;
        for (const n of r.failed_n ?? []) { const idx = batch[n]; if (idx != null) left.push(ready[idx]!); }
        if (r.failed_n?.length) err = "Часть снимков хранилище не приняло";
      } catch (e) {
        /* 4xx (кроме 408/429) — сервер отверг сами файлы: повтор того же не поможет, в «Повторить» не кладём */
        const st = e instanceof ApiException ? e.status : 0;
        if (st >= 400 && st < 500 && st !== 408 && st !== 429) { rejected += batch.length; err = errText(e, "Не удалось отправить"); }
        else {
          /* исход неизвестен (обрыв, таймаут, 5xx посреди пачки): часть могла лечь — повтор с теми же ключами её не задвоит */
          batch.forEach((idx) => left.push(ready[idx]!));
          err = `${ac.signal.aborted ? "Ответ не пришёл за 2 минуты" : st === 502 ? "Хранилище не приняло снимки"
            : st ? errText(e, "Сервер не ответил") : "Связь оборвалась"} — «Повторить» дошлёт, уже сохранённые не задвоятся`;
        }
      } finally { window.clearTimeout(tm); }
      setBusy((b) => (b ? { ...b, done: b.done + batch.length } : b));
    }
    return { sent, left, rejected, err };
  }, [ref]);

  /* retry — неотправленные целиком заменяются ответом; новая загрузка — дописывается к ещё не повторённым */
  const finish = useCallback((r: Sent, bad: number, badErr: string | null, retried: boolean) => {
    setBusy(null); setPending((p) => (retried ? r.left : [...p, ...r.left]));
    const parts = [r.sent ? `Загружено: ${r.sent}` : "", r.left.length ? `не отправлено: ${r.left.length}` : "", r.rejected ? `не принято: ${r.rejected}` : "",
      bad ? `не обработано: ${bad}${badErr ? ` (${badErr})` : ""}` : ""].filter(Boolean);
    setNote(r.err || bad ? `${parts.join(", ")}${r.err ? `. ${r.err}` : ""}` : null);
    if (r.sent) { reload(); onChanged(); }
  }, [reload, onChanged]);

  const upload = useCallback(async (picked: File[]) => {
    if (!picked.length || busy) return;
    setNote(null);
    const ready: ShrunkShot[] = [];
    let bad = 0, badErr: string | null = null;
    /* по одному: на телефоне холст на 16 Мп и так тяжёлый, параллельно — вылет вкладки */
    for (let i = 0; i < picked.length; i++) {
      setBusy({ stage: "shrink", done: i, total: picked.length });
      try { ready.push(await shrinkShot(picked[i]!, i)); } catch (e) { bad++; badErr = errText(e, "Не удалось обработать снимок"); }
    }
    finish(ready.length ? await send(ready) : { sent: 0, left: [], rejected: 0, err: null }, bad, badErr, false);
  }, [busy, send, finish]);

  const retry = useCallback(async () => {
    if (!pending.length || busy) return;
    setNote(null);
    finish(await send(pending), 0, null, true);
  }, [pending, busy, send, finish]);

  const remove = useCallback(async (id: number) => {
    await api(`/api/ny/shots/${id}`, { method: "DELETE" });
    setItems((p) => p?.filter((s) => s.id !== id) ?? p); onChanged();
  }, [setItems, onChanged]);

  return { shots: items, error, busy, pending: pending.length, note, upload, retry, remove };
}

/* Не больше 3 файлов качаем разом: у клиента бывает 20+ скринов, каждый — целый файл с расшифровкой на сервере. */
let active = 0;
const queue: (() => void)[] = [];
async function limited<T>(job: () => Promise<T>): Promise<T> {
  if (active >= 3) await new Promise<void>((res) => queue.push(res));
  active++;
  try { return await job(); } finally { active--; queue.shift()?.(); }
}

/** Скрин → objectURL (освобождается при размонтировании); null — ещё грузится, "" — не загрузился. */
export function useShotUrl(id: number): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true; let u: string | null = null;
    limited(() => (alive ? apiFileUrl(`/api/ny/shots/${id}/file`) : Promise.resolve("")))
      .then((x) => { if (alive) { u = x || null; setUrl(x); } else if (x) URL.revokeObjectURL(x); })
      .catch(() => { if (alive) setUrl(""); });
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return url;
}
